import React from 'react';
import { useDispatch } from 'react-redux';
import { Button, DialogActions, DialogContent, DialogTitle, Grid } from '@mui/material';

import { closeDialog, openDialog } from 'app/store/fuse/dialogSlice';
import { AuthorizerContext } from 'app/providers/acl/AclProvider';
import { Form, useIsModuleDisabled, useModel, useSettings, useUserData, Utils } from 'app/main/components';
import {
	BooleanField,
	CurrencyTextField,
	DateField,
	ModuleSearchField,
	SimpleTextField
} from 'app/main/components/fields';

interface ContractsLinesEditDialogProps {
	baseModel: any;
	isAdd?: boolean;
	updateRefresh: VoidFunction;
}

export function showContractsLinesEditDialog(props: ContractsLinesEditDialogProps) {
	return openDialog({
		children: <ContractsLinesEditDialog {...props} />,
		maxWidth: 'sm',
		fullWidth: true
	});
}

export default function ContractsLinesEditDialog(props: ContractsLinesEditDialogProps) {
	const dispatch = useDispatch();
	const { settings, t } = useSettings('contracts_lines');
	const userData = useUserData();
	const isModuleDisabled = useIsModuleDisabled();
	const authorizer = React.useContext(AuthorizerContext);

	const { model, modifiedModel, sendChange, save } = useModel({
		baseModel: props.baseModel,
		settings,
		isEdit: true,
		isAdd: props.isAdd
	});

	function saveModel() {
		if (!props.isAdd) {
			save({
				onSuccess: () => {
					dispatch(Utils.alertSuccessMsg(t));
					dispatch(closeDialog());
					props.updateRefresh();
				}
			});
		} else {
			dispatch(Utils.alertProgressMsg(t));
			Utils.executeStored({
				action: 'st_contracts_lines_add',
				dispatch,
				data: {
					cnli_cntr_uid: model.cnli_cntr_uid,
					cnli_user_created_uid: userData.user_uid,
					lines: [modifiedModel]
				},
				successHandler: () => {
					dispatch(Utils.alertSuccessMsg(t));
					dispatch(closeDialog());
					props.updateRefresh();
				}
			});
		}
	}

	return (
		<>
			<DialogTitle>
				{t(
					props.isAdd ? 'DIALOG_CONTRACTS_LINES_DIALOG_ADD_TITLE' : 'DIALOG_CONTRACTS_LINES_DIALOG_EDIT_TITLE'
				)}
			</DialogTitle>
			<DialogContent>
				<Form id="contractsLinesEditDialog" onSubmit={saveModel}>
					<Grid container spacing={1}>
						<Grid size={{ sm: 6, xs: 12 }}>
							<DateField
								name="cnli_d_billing"
								label={t('DETAILS_CONTRACTS_LINES_FIELD_BILLING_DATE')}
								value={model.cnli_d_billing}
								isEdit
								sendChange={sendChange}
								required
								validationErrors={{ isDefaultRequiredValue: t('ERROR_TEXT_REQUIRED') }}
							/>
						</Grid>
						<Grid size={{ sm: 6, xs: 12 }}>
							<CurrencyTextField
								name="cnli_amount"
								label={t('DETAILS_CONTRACTS_LINES_FIELD_AMOUNT')}
								value={model.cnli_amount}
								isEdit
								sendChange={sendChange}
								required
								validationErrors={{ isDefaultRequiredValue: t('ERROR_TEXT_REQUIRED') }}
							/>
						</Grid>
						<Grid size={12}>
							<SimpleTextField
								name="cnli_description"
								label={t('DETAILS_CONTRACTS_LINES_FIELD_DESCRIPTION')}
								value={model.cnli_description}
								isEdit
								sendChange={sendChange}
								required
								validationErrors={{ isDefaultRequiredValue: t('ERROR_TEXT_REQUIRED') }}
							/>
						</Grid>
						<Grid size={{ sm: 6, xs: 12 }}>
							<BooleanField
								name="cnli_billed"
								label={t('DETAILS_CONTRACTS_LINES_FIELD_BILLED')}
								value={model.cnli_billed}
								isEdit
								sendChange={sendChange}
							/>
						</Grid>
						{!isModuleDisabled('invoices') && authorizer.can('view', 'invoices') ? (
							<Grid size={{ sm: 6, xs: 12 }}>
								<ModuleSearchField
									name="cnli_invc_uid"
									label={t('DETAILS_CONTRACTS_LINES_FIELD_INVOICE')}
									module="invoices"
									uid={model.cnli_invc_uid}
									isEdit={!!model.cnli_billed}
									sendChange={sendChange}
									required={!!model.cnli_billed}
									validationError={t('ERROR_TEXT_REQUIRED')}
								/>
							</Grid>
						) : (
							<Grid size={{ sm: 6, xs: 12 }}>
								<DateField
									name="cnli_d_billed"
									label={t('DETAILS_CONTRACTS_LINES_FIELD_DATE_BILLED')}
									value={model.cnli_d_billed_final}
									isEdit={!!model.cnli_billed}
									sendChange={sendChange}
									afterChange={newValue =>
										sendChange('cnli_d_billed_final', newValue?.toISOString() || null)
									}
									required={!!model.cnli_billed}
									validationErrors={{ isDefaultRequiredValue: t('ERROR_TEXT_REQUIRED') }}
								/>
							</Grid>
						)}
					</Grid>
				</Form>
			</DialogContent>
			<DialogActions>
				<Button onClick={() => dispatch(closeDialog())}>{t('DIALOG_ACTION_CANCEL')}</Button>
				<Button type="submit" form="contractsLinesEditDialog" color="secondary">
					{t('DIALOG_ACTION_CONFIRM')}
				</Button>
			</DialogActions>
		</>
	);
}
